import SearchBar from '@/Search/components/SearchBar.vue';
import { computed, defineComponent, onMounted, onUnmounted, ref } from '@vue/composition-api';
import HighlightedLocations from '@/Home/HighlightedLocations.vue';
import Testimonials from '@/Home/Testimonials.vue';
import { useGetLocalizedPath } from '@/util/globalHelpers';
import WhatWeDoBlock from '@/Home/WhatWeDoBlock.vue';
import useImageResize from '@/shared/composables/useImageResize';
import { GeoPagesRoutes } from '@/GeoPages/geopages.const';
import HomeInfo from '@/Home/HomeInfo.vue';
import { SITE_URL } from '@/App.vue';
import { socialSharingOptions, homeInfoBlocks } from '@/Home/home.const';
import { applyCMSImageFilters } from '@/StoryBlok/helpers';
import { imageSrcSet } from '@/util/images';
import { useToast } from 'vue-toastification/composition';
// Local brand hero, used whenever the CMS has no hero image for the page.
// 16/9, to match imageHeight below.
const HERO_FALLBACK = '/img/brand/hero.jpg';
export default defineComponent({
    components: {
        HomeInfo,
        HighlightedLocations,
        SearchBar,
        Testimonials,
        WhatWeDoBlock
    },
    props: {
        blok: {
            type: Object,
            default: () => { }
        }
    },
    setup(props, context) {
        const { root } = context;
        const url = ref(`${SITE_URL}${root.$router.currentRoute.path}`);
        const img = ref(null);
        const { imageWidth, onScreenResize } = useImageResize(img, {
            defaultWidth: 1280,
            beginResizingAt: 1320,
            widthModificator: 40
        });
        const imgSrc = computed(() => {
            if (!props.blok.hero_image || !props.blok.hero_image.filename) {
                // Directus currently holds no files at all (directus_files is empty),
                // so this branch is the live one and returning '' left the homepage
                // with a blank hero. Fall back to the brand image rather than nothing;
                // a seeded CMS still wins.
                return HERO_FALLBACK;
            }
            // https://www.storyblok.com/docs/Guides/how-to-resize-images
            return applyCMSImageFilters(props.blok.hero_image.filename, imageWidth.value);
        });
        const imageHeight = computed(() => {
            const aspectRatio = 16 / 9;
            return Math.round(imageWidth.value / aspectRatio);
        });
        // Empty for a local file -- there is no service behind /img/* to resize
        // it -- and the browser ignores an empty srcset, so `src` carries the load
        // exactly as before. That is the case on this deployment today: the hero
        // is a mock image at /img/home/hero.jpg. It starts doing real work the
        // moment the hero comes from a media service again.
        const imgSrcSet = computed(() => imageSrcSet(imgSrc.value, [640, 960, 1280, 1920], { aspect: 16 / 9 }));
        const toast = useToast();
        const copyLink = () => {
            root.$copyText(SITE_URL);
            toast.success(root.$i18n.t('common.social_sharing.link_copied'));
        };
        const goToLocations = () => globalThis.$router.push(useGetLocalizedPath(GeoPagesRoutes.CITIES));
        onMounted(() => {
            window.addEventListener('resize', onScreenResize);
            onScreenResize();
        });
        onUnmounted(() => {
            window.removeEventListener('resize', onScreenResize);
        });
        return {
            homeInfoBlocks,
            imageHeight,
            imageWidth,
            img,
            imgSrc,
            imgSrcSet,
            socialSharingOptions,
            url,
            copyLink,
            goToLocations
        };
    }
});
